import { useCallback, useEffect, useState } from "react";
import { IPrimaryUser } from "services/api/api.types";
import {
  FocusExam,
  SubscribedCourse
} from "services/api/sessions/sessionService.types";
import {
  getFocusExamsOfUser,
  getFreeSessionsForUser,
  getSessionsForUser
} from "services/api/sessions/sessionsService";
import { DaySchedule, Sessions } from "./use-live-classes.types";
import * as Sentry from "@sentry/react";
import {
  getWeeklyClassSchedule,
  CourseScheduleItem
} from "components/kelas-live/class-schedule/class-schedule-utils";
import { useKelasLiveContext } from "stores/kelas-live/kelas-live-context";

const mapSubscribedCoursesToScheduleItems = (
  courses: SubscribedCourse[]
): CourseScheduleItem[] => {
  if (!courses?.length) return [];
  return [
    {
      isActive: true,
      schedules: courses.map((c) => ({
        days: c.days ?? [],
        course: c.course,
        startTime: c.startTime ?? "",
        endTime: c.endTime ?? ""
      }))
    }
  ];
};

export const useLiveClasses = (
  primaryUser: IPrimaryUser
): {
  sessions: Sessions;
  focusExams: FocusExam[] | null;
  focusExamIds: string[] | null;
  isFetchingSessions: boolean;
  classSchedule: DaySchedule[];
} => {
  const [sessions, setSessions] = useState<Sessions>({
    courseMappedSessions: null,
    gradeMappedSessions: null
  });
  const [isFetchingSessions, setIsFetchingSessions] = useState(false);
  const [focusExams, setFocusExams] = useState<Array<FocusExam> | null>(null);
  const [focusExamIds, setFocusExamIds] = useState<Array<string> | null>(null);
  const [classSchedule, setClassSchedule] = useState<Array<DaySchedule>>([]);

  const { userSubscription } = useKelasLiveContext();

  const fetchSessionsForUser = useCallback(
    async (focusExamIds?: Array<string>) => {
      try {
        const response = await getSessionsForUser(
          primaryUser.access_token,
          focusExamIds
        );
        return response;
      } catch (err) {
        Sentry.captureException(err);
      }
    },
    [primaryUser.access_token]
  );

  useEffect(() => {
    try {
      const subscribedCourses =
        userSubscription.subscriptionStatus?.subscribedCourses ?? [];
      const mediaBaseUrl =
        userSubscription.subscriptionStatus?.mediaBaseUrl ?? "";
      setClassSchedule(
        getWeeklyClassSchedule(
          mapSubscribedCoursesToScheduleItems(subscribedCourses),
          mediaBaseUrl
        )
      );
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [userSubscription.subscriptionStatus]);

  const fetchFocusExamsForUser = useCallback(async () => {
    try {
      const response = await getFocusExamsOfUser(primaryUser.access_token);
      setFocusExams(response.data.focusExams);
      return response.data.focusExams;
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [primaryUser.access_token]);

  const fetchSessions = useCallback(async () => {
    try {
      setIsFetchingSessions(true);
      const fetchedFocusExams = await fetchFocusExamsForUser();
      const focusExamIds =
        fetchedFocusExams && fetchedFocusExams.map((focusExam) => focusExam.id);
      focusExamIds && setFocusExamIds(focusExamIds);

      const [courseMappedSessions, gradeMappedSessions] = await Promise.all([
        fetchSessionsForUser(focusExamIds),
        fetchFreeSessionsForUser()
      ]);

      // Set sessions data
      setSessions({
        courseMappedSessions: courseMappedSessions
          ? courseMappedSessions.data
          : null,
        gradeMappedSessions: gradeMappedSessions
          ? gradeMappedSessions.data
          : null
      });
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setIsFetchingSessions(false);
    }
  }, [fetchSessionsForUser, fetchFocusExamsForUser]);

  const fetchFreeSessionsForUser = useCallback(async () => {
    try {
      const response = await getFreeSessionsForUser(primaryUser);
      return response;
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [primaryUser.access_token]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions, primaryUser.access_token]);

  return {
    sessions,
    focusExams,
    focusExamIds,
    isFetchingSessions,
    classSchedule
  };
};
